import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import TimeAgo from "javascript-time-ago";
import en from "javascript-time-ago/locale/en.json";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import type { CurrentUser } from "api/types";
import { Alert, Container, Spinner } from "react-bootstrap";
import Link from "next/link";
import {
  AAThemeProvider,
  initColorMode,
  Sidebar,
  ToastBox,
  ToastManagerProvider,
} from "@bagel-web/components";
import { getIsActive, useNavConfig } from "utils/navConfig";
import {
  initUserContext,
  useLocalStorage,
  getQueryClientDefaultOptions,
  LocalStorageKeys,
} from "@bagel-web/common";
import { usePathname } from "next/navigation";
import useFeatureFlags from "utils/useFeatureFlags";
import NavHeader from "./NavHeader";

const queryClient = new QueryClient(getQueryClientDefaultOptions());

TimeAgo.addDefaultLocale(en);

type LayoutProps = {
  children: React.ReactNode;
  whoami: CurrentUser;
};

// TODO: Refactor withAuthentication to be shared across Admin, Dashboard and Skunkworks.
// CurrentUser (whoami) can be templated to make this generic.
const withAuthentication = (Layout: React.ComponentType<LayoutProps>) => {
  const AuthenticatedLayout = ({ children }: { children: React.ReactNode }) => {
    const [whoami, _setWhoAmI] = useState<CurrentUser | null>(null);
    const [error, setError] = useState<Error | null>(null);
    const { initialize: initializeFeatureFlags } = useFeatureFlags({
      queryClient,
    });

    const router = useRouter();

    const setWhoAmI = (currentUser: CurrentUser | null) => {
      initUserContext({
        userId: currentUser?.id || null,
        organization: null,
        projectId: null,
        roles: currentUser?.roles || [],
        permissions: currentUser?.permissions || [],
      });
      _setWhoAmI(currentUser);
    };

    useEffect(() => {
      const authenticate = async () => {
        let response;
        try {
          response = await fetch("/api/auth/whoami");
          const jsonBody = await response.json();
          const { status } = response;
          if (status === 200) {
            setWhoAmI(jsonBody);
            initializeFeatureFlags();
          } else if (status === 401) {
            const redirectPath =
              typeof jsonBody?.redirect_path === "string" &&
              jsonBody.redirect_path.startsWith("/")
                ? jsonBody.redirect_path
                : "/auth/login";
            router.push(
              redirectPath +
                "?path=" +
                encodeURIComponent(
                  window.location.pathname + window.location.search
                )
            );
          } else {
            throw new Error(`Unexpected response status ${status}`);
          }
        } catch (e) {
          setError(
            e instanceof Error ? e : new Error("Unable to fetch current user.")
          );
        }
      };

      authenticate();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      // Do this before authentication is completed to avoid flickering in layout.
      initColorMode();
    }, []);

    if (error) {
      return (
        <Container>
          <Alert variant="warning">
            Authentication error: {error?.message}
          </Alert>
        </Container>
      );
    }

    if (!whoami) {
      return (
        <div>
          <Spinner size="sm" /> Loading...
        </div>
      );
    }
    return <Layout whoami={whoami}>{children}</Layout>;
  };
  return AuthenticatedLayout;
};

const ClientLayout = ({ children, whoami }: Readonly<LayoutProps>) => {
  const navConfig = useNavConfig({ whoAmI: whoami });
  const pathname = usePathname();
  const router = useRouter();
  const localStorage = useLocalStorage<boolean>();

  const darkModeStoredPreference = localStorage.getItem(
    LocalStorageKeys.DARK_MODE
  );

  const handleUpdateDarkModeStoredPreference = (darkMode: boolean) => {
    localStorage.setItem(LocalStorageKeys.DARK_MODE, darkMode);
  };

  const handleLogOut = () => {
    router.push("/auth/logout");
  };
  return (
    <QueryClientProvider client={queryClient}>
      <AAThemeProvider
        darkModeStoredPreference={darkModeStoredPreference}
        updateDarkModeStoredPreference={handleUpdateDarkModeStoredPreference}
      >
        <ToastManagerProvider>
          <div className="vh-100 d-flex flex-column align-items-stretch">
            <NavHeader whoami={whoami} onLogOut={handleLogOut} />
            <div
              className="d-flex flex-row align-items-stretch"
              style={{ flexGrow: 1, flexShrink: 1, overflow: "hidden" }}
            >
              <Sidebar
                config={navConfig}
                LinkComponent={Link}
                getIsActive={getIsActive(pathname)}
              />
              <ToastBox />
              <main style={{ flexGrow: 1, overflow: "hidden" }}>
                {children}
              </main>
            </div>
          </div>
        </ToastManagerProvider>
      </AAThemeProvider>
    </QueryClientProvider>
  );
};

export default withAuthentication(ClientLayout);
